import Head from 'next/head';

import { ListingDetail } from '@components/programme/ListingDetail';
import { Seo } from '@components/seo/Seo';

import { serverSideWithSiteData } from '@common/utils/page';
import { getEventMetaDescription, getEventUrl } from '@common/utils/programme';
import { queryFromDato } from '@data/datocms/client';
import { elsewherePresentsDetail } from '@data/datocms/queries';
import { supabaseAdminClient } from '@data/supabase/adminClient';
import { getEvent } from '@data/supabase/getEvent';
import { getEventList } from '@data/supabase/getEventList';
import {
	ElsewherePresentsVenueRecord,
	ElsewherePresentsVenueResponse,
	ProgrammeDetailResponse,
	SiteData,
	SupabaseEventRecord,
} from '@interfaces';

interface ElsewherePresentsEventDetailPageProps
	extends ProgrammeDetailResponse {
	elsewherePresentsVenue: ElsewherePresentsVenueRecord | null;
	event: SupabaseEventRecord;
	relatedEvents: SupabaseEventRecord[];
}

const ElsewherePresentsEventDetailPage = ({
	elsewherePresentsVenue,
	event,
	site,
	programme,
	relatedEvents,
}: ElsewherePresentsEventDetailPageProps & SiteData) => {
	const url = getEventUrl({ event });
	return (
		<>
			<Head>
				<link href={url.href} rel="canonical" />
			</Head>
			<Seo
				metaTagData={[
					...site.faviconMetaTags,
					{
						__typename: 'Tag',
						tag: 'title',
						content: event.name,
						attributes: {},
					},
					{
						__typename: 'Tag',
						tag: 'meta',
						content: null,
						attributes: {
							name: 'description',
							content: getEventMetaDescription(event),
						},
					},
				]}
			/>
			<ListingDetail
				elsewherePresentsVenue={elsewherePresentsVenue}
				event={event}
				programmeData={programme}
				relatedEvents={relatedEvents}
			/>
		</>
	);
};

export const getServerSideProps =
	serverSideWithSiteData<ElsewherePresentsEventDetailPageProps>(
		async (context) => {
			const { slug } = context.params || {};

			if (typeof slug !== 'string') {
				return {
					notFound: true,
				};
			}

			const event = await getEvent({
				options: {
					elsewherePresents: true,
					excludePast: false,
					slug,
				},
				supabaseClient: supabaseAdminClient,
			});

			if (!event) {
				return {
					notFound: true,
				};
			}

			// Get three other Elsewhere Presents events but exclude the current event
			const { events: relatedEvents } = await getEventList({
				options: {
					elsewherePresents: true,
					excludeDice: true,
					excludeIds: [event.id],
					excludePast: false,
					range: {
						from: 0,
						to: 2,
					},
				},
				supabaseClient: supabaseAdminClient,
			});

			const datoResponse = await queryFromDato<
				ProgrammeDetailResponse & ElsewherePresentsVenueResponse
			>({
				query: elsewherePresentsDetail,
				variables: {
					venueTitle: event.venues[0] || '',
				},
			});

			if (!datoResponse) {
				return {
					notFound: true,
				};
			}

			return {
				props: {
					programme: datoResponse.programme,
					elsewherePresentsVenue: datoResponse.elsewherePresentsVenue,
					event,
					relatedEvents,
				},
			};
		},
	);

export default ElsewherePresentsEventDetailPage;
